<template>
  <div class="container-fluid py-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h4 class="mb-0">
        <feather-icon
          icon="ActivityIcon"
          size="20"
          class="mr-50"
        />
        {{ $t('regulacao.title') }}
      </h4>
      <button
        v-if="$hasAbility('regulacao.atualizar')"
        class="btn btn-sm btn-outline-secondary"
        @click="loadOccurrences"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('regulacao.refresh') }}
      </button>
    </div>

    <!-- KPI Cards -->
    <div class="row mb-3">
      <div
        v-for="card in kpiCards"
        :key="card.key"
        class="col-6 col-md-4 col-xl-2 mb-2"
      >
        <div
          class="kpi-card"
          :class="[
            card.variant,
            {
              'kpi-card--clickable': card.filterable,
              'kpi-card--active': card.filterable && filtroKpi === card.key,
            },
          ]"
          :role="card.filterable ? 'button' : null"
          :tabindex="card.filterable ? 0 : null"
          :aria-pressed="card.filterable ? String(filtroKpi === card.key) : null"
          @click="toggleKpi(card)"
          @keydown.enter.prevent="toggleKpi(card)"
          @keydown.space.prevent="toggleKpi(card)"
        >
          <div class="kpi-card__icon">
            <feather-icon
              :icon="card.icon"
              size="20"
            />
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">
              <b-spinner
                v-if="loadingKpis"
                small
              />
              <span v-else>{{ card.value }}</span>
            </div>
            <div class="kpi-card__label">
              {{ card.label }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="filtroKpi"
      class="kpi-filter mb-3"
    >
      <feather-icon
        icon="FilterIcon"
        size="14"
      />
      <span>
        {{ $t('regulacao.filteringBy') }} <strong>{{ rotuloFiltroKpi }}</strong> &mdash;
        {{ $t('regulacao.occurrencesCount', { count: nodeOccurrences.length }) }}
      </span>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary ml-auto"
        @click="limparFiltroKpi"
      >
        <feather-icon
          icon="XIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('regulacao.clearFilter') }}
      </button>
    </div>

    <occurrence-filter-bar
      v-model="filterText"
      :applied="appliedTerm"
      :loading="loadingMy || loadingNode"
      :count="nodeOccurrences.length"
      @submit="applyFilter"
    />

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">
        {{ $t('regulacao.loadingOccurrences') }}
      </p>
    </div>

    <template v-else>
      <!-- Minhas Ocorrências -->
      <div class="card mb-3">
        <div
          class="card-header d-flex align-items-center justify-content-between py-2"
        >
          <span class="font-weight-bold">
            <feather-icon
              icon="UserIcon"
              size="15"
              class="mr-50"
            />
            {{ $t('regulacao.myOccurrences') }}
          </span>
          <span
            v-if="$hasAbility('regulacao.minhas_oc')"
            class="badge badge-primary"
          >{{ myOccurrences.length }}</span>
        </div>
        <div class="card-body p-0">
          <occurrence-table
            :occurrences="myOccurrences"
            :empty-text="$t('regulacao.emptyMyOccurrences')"
            @open="abrirOcorrencia"
            @change="onMyQueryChange"
          />
        </div>
      </div>

      <!-- Ocorrências da Central -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between py-2"
        >
          <span class="font-weight-bold">
            <feather-icon
              icon="GridIcon"
              size="15"
              class="mr-50"
            />
            {{ $t('regulacao.centralOccurrences') }}
          </span>
          <span
            v-if="$hasAbility('regulacao.oc_central')"
            class="badge badge-secondary"
          >{{ nodeOccurrences.length }}</span>
        </div>
        <div class="card-body p-0">
          <occurrence-table
            :occurrences="nodeOccurrences"
            highlight-mine
            empty-icon="InboxIcon"
            :empty-text="$t('regulacao.emptyNodeOccurrences')"
            @open="abrirOcorrencia"
            @change="onNodeQueryChange"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import { BSpinner } from 'bootstrap-vue'
import OccurrenceTable from '@/components/OccurrenceTable.vue'
import RealtimeResyncMixin from '@/mixins/RealtimeResyncMixin'
import OccurrenceFeedMixin from '@/mixins/OccurrenceFeedMixin'
import OccurrenceFilterMixin from '@/mixins/OccurrenceFilterMixin'
import OccurrenceFilterBar from '@/components/OccurrenceFilterBar.vue'

export default {
  name: 'RegulacaoIndex',
  components: { BSpinner, OccurrenceTable, OccurrenceFilterBar },
  mixins: [ApiErrorMixin, RealtimeResyncMixin, OccurrenceFeedMixin, OccurrenceFilterMixin],
  data() {
    return {
      myOccurrences: [],
      nodeOccurrences: [],
      loadingMy: false,
      loadingNode: false,
      loading: false,
      myQuery: { sortBy: null, sortDir: 'asc' },
      nodeQuery: { sortBy: null, sortDir: 'asc' },
      loadingKpis: false,
      refreshTimer: null,
      kpis: {},
      filtroKpi: null,
      myRequestId: 0,
      nodeRequestId: 0,
    }
  },
  computed: {
    kpiCards() {
      const k = this.kpis
      return [
        {
          key: 'total_open',
          filterable: true,
          label: this.$t('regulacao.kpi.totalOpen'),
          value: k.total_open ?? '—',
          icon: 'AlertCircleIcon',
          variant: 'kpi-card--primary',
        },
        {
          key: 'my_open',
          filterable: true,
          label: this.$t('regulacao.kpi.myOpen'),
          value: k.my_open ?? '—',
          icon: 'UserIcon',
          variant: 'kpi-card--info',
        },
        {
          key: 'without_responsible',
          filterable: true,
          label: this.$t('regulacao.kpi.pending'),
          value: k.without_responsible ?? '—',
          icon: 'AlertTriangleIcon',
          variant:
            k.without_responsible > 0
              ? 'kpi-card--danger'
              : 'kpi-card--success',
        },
        {
          key: 'last_1h',
          filterable: false,
          label: this.$t('regulacao.kpi.last1h'),
          value: k.last_1h ?? '—',
          icon: 'ClockIcon',
          variant: 'kpi-card--secondary',
        },
        {
          key: 'last_24h',
          filterable: false,
          label: this.$t('regulacao.kpi.last24h'),
          value: k.last_24h ?? '—',
          icon: 'CalendarIcon',
          variant: 'kpi-card--secondary',
        },
        {
          key: 'avg_seconds_to_assume',
          filterable: false,
          label: this.$t('regulacao.kpi.avgTimeToAssume'),
          value:
            k.avg_seconds_to_assume != null
              ? this.formatSeconds(k.avg_seconds_to_assume)
              : '—',
          icon: 'ZapIcon',
          variant: 'kpi-card--secondary',
        },
      ]
    },
    rotuloFiltroKpi() {
      const card = this.kpiCards.find(c => c.key === this.filtroKpi)
      return card ? card.label : ''
    },
  },
  mounted() {
    this.loadOccurrences()
    this.loadKpis()
    this.refreshTimer = setInterval(() => {
      this.loadOccurrences()
      this.loadKpis()
    }, 180000)

    this.watchOccurrenceFeed()
    this.watchRealtimeResync(() => this.reloadQueue())
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  },
  methods: {
    abrirOcorrencia(occ) {
      this.$router.push({ name: 'regulacao-detail', params: { id: occ.id } })
    },

    limparFiltroKpi() {
      this.filtroKpi = null
      this.loadOccurrences()
    },

    toggleKpi(card) {
      if (!card.filterable) return

      this.filtroKpi = this.filtroKpi === card.key ? null : card.key
      this.loadOccurrences()
    },

    onFilterApplied() {
      this.loadOccurrences()
    },

    reloadQueue() {
      this.loadOccurrences()
      this.loadKpis()
    },

    applyOccurrenceFeed() {
      this.scheduleRealtimeReload(() => {
        this.clearOccurrenceFeed()
        this.reloadQueue()
      })
    },
    onMyQueryChange(query) {
      this.myQuery = query
      this.loadMyOccurrences()
    },

    onNodeQueryChange(query) {
      this.nodeQuery = query
      this.loadNodeOccurrences()
    },

    baseParams(query) {
      return {
        with: ['activeResponsibility.user', 'classification'],
        responsibility_step: 'regulacao',
        step_since: 'regulacao',
        context: 'regulacao',
        current_step: ['regulacao', 'despacha', 'liberado'],
        search: this.appliedTerm || undefined,
        sort_by: query.sortBy || undefined,
        sort_dir: query.sortDir || undefined,
      }
    },

    async loadOccurrences() {
      this.loading = !this.myOccurrences.length && !this.nodeOccurrences.length

      try {
        await Promise.all([this.loadMyOccurrences(), this.loadNodeOccurrences()])
      } finally {
        this.loading = false
      }
    },

    async loadMyOccurrences() {
      if (this.filtroKpi === 'without_responsible') {
        this.myRequestId += 1
        this.loadingMy = false
        this.myOccurrences = []
        return
      }

      this.myRequestId += 1
      const requestId = this.myRequestId
      this.loadingMy = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrences`, {
          params: { ...this.baseParams(this.myQuery), responsible: 'me' },
        })
        if (requestId !== this.myRequestId) return
        this.myOccurrences = res.data.data || []
      } catch (e) {
        if (requestId !== this.myRequestId) return
        this.$message(this.$t('regulacao.messages.errorTitle'), this.apiErrorText(e, this.$t('regulacao.messages.errorLoadMyOccurrences')), 'error')
      } finally {
        if (requestId === this.myRequestId) this.loadingMy = false
      }
    },

    async loadNodeOccurrences() {
      this.nodeRequestId += 1
      const requestId = this.nodeRequestId
      this.loadingNode = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrences`, {
          params: {
            ...this.baseParams(this.nodeQuery),
            responsible: { without_responsible: 'none', my_open: 'me' }[this.filtroKpi],
          },
        })
        if (requestId !== this.nodeRequestId) return
        this.nodeOccurrences = res.data.data || []
      } catch (e) {
        if (requestId !== this.nodeRequestId) return
        this.$message(this.$t('regulacao.messages.errorTitle'), this.apiErrorText(e, this.$t('regulacao.messages.errorLoadNodeOccurrences')), 'error')
      } finally {
        if (requestId === this.nodeRequestId) this.loadingNode = false
      }
    },

    async loadKpis() {
      this.loadingKpis = Object.keys(this.kpis).length === 0
      try {
        const res = await this.$http.get(
          `${this.$store.state.api}occurrences/step-kpis`,
          {
            params: { step: 'regulacao' },
          },
        )
        this.kpis = res.data.data || {}
      } catch (_) {
        // KPI e informativo: falhar nele nao pode derrubar a fila
      } finally {
        this.loadingKpis = false
      }
    },
    formatSeconds(seconds) {
      if (seconds == null) return '—'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      if (h > 0) return `${h}h ${m}m`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },
  },
}
</script>

<style scoped>
.kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
}

.kpi-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-card__value {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.kpi-card__label {
  font-size: 11px;
  color: #b9b9c3;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* Variantes */
.kpi-card--primary .kpi-card__icon {
  background: #ede9fd;
  color: #7367f0;
}
.kpi-card--primary .kpi-card__value {
  color: #7367f0;
}

.kpi-card--info .kpi-card__icon {
  background: #d4f4e6;
  color: #00cfe8;
}
.kpi-card--info .kpi-card__value {
  color: #00cfe8;
}

.kpi-card--danger {
  border-color: #ea5455;
  border-width: 2px;
  background: #fff5f5;
  animation: kpi-pulse 2s ease-in-out infinite;
}
.kpi-card--danger .kpi-card__icon {
  background: #ea5455;
  color: #fff;
  width: 48px;
  height: 48px;
}
.kpi-card--danger .kpi-card__value {
  color: #ea5455;
  font-size: 1.9rem;
}
.kpi-card--danger .kpi-card__label {
  color: #ea5455;
  font-weight: 700;
}

@keyframes kpi-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.25);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(234, 84, 85, 0);
  }
}

.kpi-card--success .kpi-card__icon {
  background: #e8faf0;
  color: #28c76f;
}
.kpi-card--success .kpi-card__value {
  color: #28c76f;
}

.kpi-card--secondary .kpi-card__icon {
  background: #f3f2f7;
  color: #6e6b7b;
}
.kpi-card--secondary .kpi-card__value {
  color: #5e5873;
}

/* Dark theme */
.dark-layout .kpi-card {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .kpi-card__label {
  color: #676d7d;
}

.kpi-card--clickable {
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease;
}
.kpi-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
}
.kpi-card--clickable:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 2px;
}
.kpi-card--active {
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.35);
}
.kpi-card--danger.kpi-card--active {
  border-color: #ea5455;
  box-shadow: 0 0 0 2px rgba(234, 84, 85, 0.35);
  animation: none;
}
.dark-layout .kpi-card--clickable:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}

.kpi-filter {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f3f2fa;
  border: 1px solid #d8d4f2;
  color: #5e5873;
  font-size: 13px;
}
.dark-layout .kpi-filter {
  background: #2f3349;
  border-color: #3b4253;
  color: #d0d2d6;
}
</style>
